import React, { useState } from 'react';
import { Car, Zap, Shield } from 'lucide-react';
import AutoMode from '@mui/icons-material/AutoMode';

const EV_KWH_PER_KM = 0.22;

export default function SEOEVCalculator({ competitorName, elSavings = 0 }) {
  const [km, setKm] = useState(20000);
  const [homePct, setHomePct] = useState(90);

  const evKwh = Math.round(km * EV_KWH_PER_KM * (homePct / 100));
  const laderabat = Math.round(evKwh * 0.10);
  const smartCharge = Math.round(evKwh * 1.45 * 0.22);
  const serviceAftale = 588;
  const totalEV = laderabat + smartCharge + serviceAftale;
  const grandTotal = elSavings + totalEV;

  return (
    <section className="py-10 bg-[#f5f3ee]">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#eab308]/20 text-[#92710a] rounded-full px-4 py-1.5 text-sm font-semibold mb-3">
              <Car className="w-4 h-4" /> Elbil-beregner
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Har du elbil? Beregn din ekstra besparelse
            </h2>
          </div>

          {/* Sliders */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">Kilometer pr. år</span>
                <span className="font-bold text-gray-900">{km.toLocaleString('da-DK')} km</span>
              </div>
              <input type="range" min="5000" max="50000" step="1000" value={km} onChange={e => setKm(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1a2e1a]" />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5.000 km</span><span>50.000 km</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">Hjemmeladning</span>
                <span className="font-bold text-gray-900">{homePct}%</span>
              </div>
              <input type="range" min="30" max="100" step="5" value={homePct} onChange={e => setHomePct(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1a2e1a]" />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>30%</span><span>100%</span>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 border-t border-gray-100 pt-3">
              Hjemmeladning: ~{evKwh.toLocaleString('da-DK')} kWh/år
            </div>
          </div>

          {/* Results */}
          <div className="mt-4 space-y-2">
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#eab308]" />
                <span className="text-sm text-gray-700">Laderabat (10 øre/kWh)</span>
              </div>
              <span className="font-bold text-[#1a2e1a]">{laderabat.toLocaleString('da-DK')} kr./år</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AutoMode sx={{ fontSize: 18, color: '#1a2e1a' }} />
                <span className="text-sm text-gray-700">Saving Mode (~22% besparelse)</span>
              </div>
              <span className="font-bold text-[#1a2e1a]">~{smartCharge.toLocaleString('da-DK')} kr./år</span>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#1a2e1a]" />
                <span className="text-sm text-gray-700">Serviceaftale (0 kr./md. vs. ~49 kr.)</span>
              </div>
              <span className="font-bold text-[#1a2e1a]">{serviceAftale.toLocaleString('da-DK')} kr./år</span>
            </div>
          </div>

          {/* Total */}
          <div className="mt-4 bg-[#1a3a32] rounded-2xl p-6 text-center text-white">
            <p className="text-sm text-white/80 mb-1">Samlet besparelse med elbil{competitorName ? ` vs. ${competitorName}` : ''}</p>
            <p className="text-4xl font-bold" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              ~{grandTotal.toLocaleString('da-DK')} kr./år
            </p>
            <div className="flex items-center justify-center gap-4 mt-3 text-sm text-white/70">
              {elSavings > 0 && <span>El: {elSavings.toLocaleString('da-DK')} kr.</span>}
              <span>EV: {totalEV.toLocaleString('da-DK')} kr.</span>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 text-center mt-3">
            Estimeret besparelse. Laderabat: 10 øre/kWh af spotpris. Saving Mode: ~22% af opladningsomkostninger. Serviceaftale: 0 kr./md. (markedsværdi ~49 kr./md.).
          </p>
        </div>
      </div>
    </section>
  );
}